import { supabase } from '../api/supabaseClient.js';

// Serviço completo de notificações
class NotificationService {
    constructor() {
        this.isInitialized = false;
        this.registration = null;
        this.subscription = null;
        this.soundEnabled = true;
        this.soundVolume = 70;
        this.audioContext = null;
        this.settings = {};
        this.realtimeChannel = null; // Armazenar referência do channel
    }

    async initialize(userId) {
        try {
            console.log('[NOTIFICATION] Inicializando NotificationService...');
            await this.loadUserSettings(userId);
            
            if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window) {
                await this.initializeServiceWorker();
            }
            
            await this.initializeAudio();
            await this.requestPermissions();
            this.setupRealtimeSubscription(userId);
            
            this.isInitialized = true;
            console.log('[NOTIFICATION] NotificationService inicializado!');
            
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao inicializar NotificationService:', error);
        }
    }

    async sendNotification(options = {}) {
        const userId = options.userId;
        const type = options.type || 'info';
        const title = options.title;
        const message = options.message;
        const channels = options.channels || ['push'];
        const data = options.data || {};
        const sound = options.sound !== false;
        
        try {
            console.log('[NOTIFICATION] Enviando notificação:', { title, type });
            
            if (sound && this.soundEnabled) {
                await this.playNotificationSound(type);
            }
            
            if (channels.includes('push')) {
                await this.sendPushNotification({ title, message, type, data });
            }
            
            console.log('[NOTIFICATION] Notificação enviada');
            
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao enviar notificação:', error);
        }
    }

    async loadUserSettings(userId) {
        try {
            const { data, error } = await supabase
                .from('user_notification_settings')
                .select('*')
                .eq('user_id', userId)
                .single();
            
            if (!data && !error) {
                await this.createDefaultSettings(userId);
                return this.loadUserSettings(userId);
            }
            
            this.settings = data || {};
            this.soundEnabled = this.settings.sound_enabled !== undefined ? this.settings.sound_enabled : true;
            this.soundVolume = this.settings.sound_volume !== undefined ? this.settings.sound_volume : 70;
            
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao carregar configurações:', error);
        }
    }

    async createDefaultSettings(userId) {
        const { data } = await supabase
            .from('user_notification_settings')
            .insert({ user_id: userId })
            .select()
            .single();
        
        return data;
    }

    async updateSettings(userId, newSettings) {
        try {
            const { data, error } = await supabase
                .from('user_notification_settings')
                .update(newSettings)
                .eq('user_id', userId)
                .select()
                .single();
            
            if (error) throw error;
            
            this.settings = Object.assign({}, this.settings, newSettings);
            this.soundEnabled = this.settings.sound_enabled;
            this.soundVolume = this.settings.sound_volume;
            
            return data;
            
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao atualizar configurações:', error);
            throw error;
        }
    }

    async initializeServiceWorker() {
        try {
            if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
                this.registration = await navigator.serviceWorker.register('/sw-notifications.js');
                console.log('[NOTIFICATION] Service Worker registrado');
            }
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao registrar Service Worker:', error);
        }
    }

    async initializeAudio() {
        try {
            if (typeof window !== 'undefined') {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                if (AudioContext) {
                    this.audioContext = new AudioContext();
                }
            }
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao inicializar áudio:', error);
        }
    }

    async requestPermissions() {
        try {
            if (typeof window !== 'undefined' && 'Notification' in window) {
                await Notification.requestPermission();
            }
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao solicitar permissões:', error);
        }
    }

    async playNotificationSound(type) {
        if (!this.soundEnabled || !this.audioContext) return;
        
        try {
            const frequencies = {
                'default': [800, 600],
                'success': [600, 800],
                'warning': [400, 600],
                'error': [300, 200],
                'reminder': [1000, 800, 600]
            };
            
            const freq = frequencies[type] || frequencies['default'];
            
            for (let i = 0; i < freq.length; i++) {
                const that = this;
                setTimeout(function() {
                    that.createTone(freq[i], 0.2);
                }, i * 300);
            }
            
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao reproduzir som:', error);
        }
    }

    createTone(frequency, duration) {
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(this.soundVolume / 100 * 0.1, this.audioContext.currentTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);
        
        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration);
    }

    async sendPushNotification(data) {
        if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
            return;
        }
        
        try {
            const notification = new Notification(data.title, {
                body: data.message,
                icon: '/favicon-192x192.png',
                tag: data.type,
                data: data.data
            });
            
            notification.onclick = function() {
                window.focus();
                if (data.data && data.data.url) {
                    window.open(data.data.url, '_blank');
                }
                notification.close();
            };
            
            setTimeout(function() {
                notification.close();
            }, 5000);
            
        } catch (error) {
            console.error('[NOTIFICATION] Erro ao enviar push notification:', error);
        }
    }

    setupRealtimeSubscription(userId) {
        try {
            // Verificar se já existe um channel ativo
            if (this.realtimeChannel) {
                console.log('[NOTIFICATION] Channel já existe, cancelando subscrição anterior...');
                this.realtimeChannel.unsubscribe();
                this.realtimeChannel = null;
            }
            
            console.log('[NOTIFICATION] Configurando nova subscrição realtime para usuário:', userId);
            
            const that = this;
            this.realtimeChannel = supabase
                .channel('notifications-' + userId + '-' + Date.now()) // Channel único
                .on('postgres_changes', {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'user_notifications',
                    filter: 'user_id=eq.' + userId
                }, function(payload) {
                    that.handleRealtimeNotification(payload.new);
                })
                .subscribe();
                
            console.log('[NOTIFICATION] Subscrição realtime configurada com sucesso');
                
        } catch (error) {
            console.log('[NOTIFICATION] Erro ao configurar realtime:', error.message);
        }
    }

    handleRealtimeNotification(notification) {
        this.sendPushNotification({
            title: notification.title,
            message: notification.message,
            type: notification.type,
            data: { id: notification.id, url: notification.action_url }
        });
    }

    // Método para limpar subscrições ativas
    cleanup() {
        try {
            if (this.realtimeChannel) {
                console.log('[NOTIFICATION] Limpando subscrição realtime...');
                this.realtimeChannel.unsubscribe();
                this.realtimeChannel = null;
            }
        } catch (error) {
            console.log('[NOTIFICATION] Erro ao limpar subscrições:', error.message);
        }
    }

    startPeriodicCheck() {
        // Verificar se as funções RPC existem antes de iniciar verificação periódica
        const that = this;
        
        // Fazer uma verificação inicial para ver se as funções existem
        supabase.rpc('get_pending_reminders').then(function(result) {
            if (!result.error) {
                console.log('[NOTIFICATION] Funções RPC encontradas, iniciando verificação periódica');
                // Se sucesso, iniciar verificação periódica
                setInterval(function() {
                    that.checkPendingReminders();
                }, 60000);
            }
        }).catch(function(error) {
            console.log('[NOTIFICATION] Funções RPC não encontradas, verificação periódica desabilitada temporariamente');
            console.log('[NOTIFICATION] Para habilitar: execute fix-notification-rpc-functions.sql no Supabase');
        });
    }

    async checkPendingReminders() {
        try {
            // Tentar chamar a função RPC, mas lidar graciosamente com erros 404
            const { data, error } = await supabase.rpc('get_pending_reminders');
            
            // Se erro 404, a função não existe ainda - só fazer log silencioso
            if (error && (error.code === 'PGRST202' || error.message.includes('404'))) {
                console.log('[NOTIFICATION] Função get_pending_reminders não encontrada - execute o script SQL de notificações');
                return;
            }
            
            if (error) {
                throw error;
            }
            
            const reminders = data || [];
            for (let i = 0; i < reminders.length; i++) {
                const reminder = reminders[i];
                await this.sendNotification({
                    userId: reminder.user_id,
                    type: 'reminder',
                    title: reminder.title,
                    message: reminder.message,
                    channels: reminder.channels
                });
                
                await supabase
                    .from('scheduled_reminders')
                    .update({ is_sent: true, sent_at: new Date().toISOString() })
                    .eq('id', reminder.id);
            }
        } catch (error) {
            // Log silencioso para erros de função não encontrada
            if (error.message && error.message.includes('404')) {
                console.log('[NOTIFICATION] Aguardando criação das funções RPC de notificações');
            } else {
                console.error('[NOTIFICATION] Erro na verificação periódica:', error);
            }
        }
    }
}

export const notificationService = new NotificationService();

export const sendNotification = function(options) {
    return notificationService.sendNotification(options);
};

export const updateNotificationSettings = function(userId, settings) {
    return notificationService.updateSettings(userId, settings);
}; 